import { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { app } from "@microsoft/teams-js";

import { initializeTeams } from "../TeamsHost";

/** Theme names as reported by Teams (app.getContext().app.theme). */
export type AppThemeName = "default" | "dark" | "contrast";

export interface AppTheme {
    themeString: AppThemeName;
    /** True for the dark and high contrast themes, which both use light text on a dark background. */
    isDark: boolean;
}

const DARK_COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

export const normalizeThemeName = (theme: string | null | undefined): AppThemeName | undefined => {
    switch (theme) {
        case "dark":
        case "contrast":
        case "default":
            return theme;
        case "light":
            return "default";
        default:
            return undefined;
    }
};

export const toAppTheme = (themeString: AppThemeName): AppTheme => ({
    themeString,
    isDark: themeString !== "default",
});

const getDarkColorSchemeQuery = (): MediaQueryList | undefined =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia(DARK_COLOR_SCHEME_QUERY)
        : undefined;

const getSystemThemeName = (): AppThemeName => (getDarkColorSchemeQuery()?.matches ? "dark" : "default");

/**
 * Theme to use before it is known whether the app runs in Teams: an explicit `?theme=` query parameter
 * (the Teams `{theme}` URL placeholder), otherwise the OS/browser color scheme.
 */
export const getInitialThemeName = (): AppThemeName => {
    const fromQuery = typeof window !== "undefined"
        ? normalizeThemeName(new URLSearchParams(window.location.search).get("theme"))
        : undefined;
    return fromQuery ?? getSystemThemeName();
};

/**
 * Tailwind uses class based dark mode (see tailwind.config.js), so `dark:` variants follow the `dark` class on <html>.
 * The color scheme is set as well so native UI (scrollbars, form controls) matches the app theme instead of the OS.
 */
export const applyThemeToDocument = (themeString: AppThemeName, root?: HTMLElement): void => {
    const target = root ?? (typeof document !== "undefined" ? document.documentElement : undefined);
    if (!target) {
        return;
    }

    const { isDark } = toAppTheme(themeString);
    target.classList.toggle("dark", isDark);
    target.style.colorScheme = isDark ? "dark" : "light";
};

const AppThemeContext = createContext<AppTheme | undefined>(undefined);

/**
 * Single source of truth for the app theme. In Teams it follows the Teams theme (including live changes),
 * in a browser it follows the OS/browser color scheme. Drives Fluent UI, Tailwind `dark:` variants and useDarkMode().
 */
export function AppThemeProvider({ children }: { children: ReactNode }) {
    const [themeString, setThemeString] = useState<AppThemeName>(getInitialThemeName);

    useEffect(() => {
        let cancelled = false;
        let removeMediaListener: (() => void) | undefined;

        const setTheme = (theme: string | undefined) => {
            if (!cancelled) {
                setThemeString(normalizeThemeName(theme) ?? "default");
            }
        };

        const followSystemTheme = () => {
            const query = getDarkColorSchemeQuery();
            if (cancelled || !query) {
                return;
            }

            const handleChange = (event: MediaQueryListEvent) => setTheme(event.matches ? "dark" : "default");
            setTheme(query.matches ? "dark" : "default");
            query.addEventListener("change", handleChange);
            removeMediaListener = () => query.removeEventListener("change", handleChange);
        };

        initializeTeams()
            .then(async (inTeams) => {
                if (cancelled) {
                    return;
                }

                if (!inTeams) {
                    followSystemTheme();
                    return;
                }

                const context = await app.getContext();
                setTheme(context.app.theme);
                app.registerOnThemeChangeHandler(setTheme);
            })
            .catch((error) => {
                console.error("Failed to resolve the Teams theme, following the system theme instead.", error);
                followSystemTheme();
            });

        return () => {
            cancelled = true;
            removeMediaListener?.();
        };
    }, []);

    // Layout effect so the Tailwind class changes in the same commit as the Fluent theme.
    useLayoutEffect(() => {
        applyThemeToDocument(themeString);
    }, [themeString]);

    const theme = useMemo(() => toAppTheme(themeString), [themeString]);

    return <AppThemeContext.Provider value={theme}>{children}</AppThemeContext.Provider>;
}

/** The current app theme. Must be used within an AppThemeProvider. */
export const useAppTheme = (): AppTheme => {
    const theme = useContext(AppThemeContext);
    if (!theme) {
        throw new Error("useAppTheme must be used within an AppThemeProvider.");
    }

    return theme;
};
